'use client';

import { LogOut, Menu as MenuIcon, Moon, Sun } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';

import { BrandMark } from './brand-mark';
import { useSession } from '@/app/session-context';
import { useTheme } from '@/app/theme-context';
import {
  ButtonLink,
  IconButton,
  Link,
  Menu,
  MenuTrigger,
  NavigationLink,
  SkipLink,
  showToast,
  ToastRegion,
  Tooltip,
  TooltipTrigger
} from '@/components/ui';
import { cx, focusTargetRing } from '@/components/ui/styles';
import { brandName } from '@/lib/events';

interface AppShellProps {
  children: ReactNode;
}

interface AppHeaderProps {
  appearance?: 'global' | 'workspace';
  brandLabel?: string;
  children?: ReactNode;
  footer?: ReactNode;
}

interface NavigationItem {
  href: string;
  label: string;
  requiresSession?: boolean;
}

const playerNavigation: readonly NavigationItem[] = [
  {
    href: '/overview',
    label: 'Overview'
  },
  {
    href: '/challenges',
    label: 'Challenges',
    requiresSession: true
  },
  {
    href: '/rules',
    label: 'Rules'
  }
];

function pathIsCurrent(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function AppHeader({
  appearance = 'global',
  brandLabel = 'Kitsune',
  children,
  footer
}: AppHeaderProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated, logout } = useSession();
  const { isDark, setPreference } = useTheme();
  const navigation = playerNavigation.filter((item) => isAuthenticated || !item.requiresSession);
  const mobileOptions = [
    ...navigation,
    ...(isAuthenticated
      ? [{ href: '/profile', label: 'Profile' }]
      : [{ href: '/login', label: 'Sign in' }])
  ].map((item) => ({
    href: item.href,
    id: item.href,
    label: item.label
  }));
  const selectedNavigationHref = navigation.find((item) =>
    pathIsCurrent(pathname, item.href)
  )?.href;

  return (
    <header
      className={
        appearance === 'workspace'
          ? 'kitsune-merged-header shrink-0 overflow-hidden rounded-md bg-surface-raised'
          : 'kitsune-global-header mx-3 mt-3 shrink-0 overflow-hidden rounded-md bg-surface-raised'
      }
    >
      <div className="flex min-h-16 w-full items-center gap-3 px-4">
        <div className="md:hidden">
          <MenuTrigger>
            <IconButton label="Open navigation">
              <MenuIcon aria-hidden className="size-5" />
            </IconButton>
            <Menu
              aria-label="Navigation"
              options={mobileOptions}
              selectedKeys={selectedNavigationHref ? [selectedNavigationHref] : []}
              selectionMode="single"
            />
          </MenuTrigger>
        </div>
        <Link
          aria-label={brandLabel}
          className="flex shrink-0 items-center gap-3 font-display text-lg font-semibold tracking-tight text-text no-underline"
          href="/overview"
          tone="current"
        >
          <BrandMark />
          {appearance === 'workspace' ? (
            <h1 className="kitsune-optical-center m-0 truncate text-lg">{brandLabel}</h1>
          ) : brandLabel !== brandName ? (
            <span className="kitsune-optical-center">{brandLabel}</span>
          ) : null}
        </Link>
        <nav aria-label="Player" className="hidden items-center gap-1 md:flex">
          {navigation.map((item) => (
            <NavigationLink
              className="px-3"
              href={item.href}
              isCurrent={pathIsCurrent(pathname, item.href)}
              key={item.href}
            >
              {item.label}
            </NavigationLink>
          ))}
        </nav>
        {children ? <div className="min-w-0 flex-1">{children}</div> : <div className="flex-1" />}
        <div className="flex shrink-0 items-center gap-1">
          {isAuthenticated ? (
            <NavigationLink
              className="hidden md:flex"
              href="/profile"
              isCurrent={pathIsCurrent(pathname, '/profile')}
            >
              Profile
            </NavigationLink>
          ) : null}
          <TooltipTrigger>
            <IconButton
              label={isDark ? 'Use light theme' : 'Use dark theme'}
              onPress={() => {
                setPreference(isDark ? 'light' : 'dark');
              }}
            >
              {isDark ? (
                <Sun aria-hidden className="size-4" />
              ) : (
                <Moon aria-hidden className="size-4" />
              )}
            </IconButton>
            <Tooltip>{isDark ? 'Use light theme' : 'Use dark theme'}</Tooltip>
          </TooltipTrigger>
          {isAuthenticated ? (
            <TooltipTrigger>
              <IconButton
                label="Sign out"
                onPress={() => {
                  void logout().then((signedOut) => {
                    if (signedOut) {
                      router.replace('/login');
                      router.refresh();
                      return;
                    }

                    showToast({
                      title: 'Sign out failed',
                      tone: 'danger'
                    });
                  });
                }}
              >
                <LogOut aria-hidden className="size-4" />
              </IconButton>
              <Tooltip>Sign out</Tooltip>
            </TooltipTrigger>
          ) : (
            <div className="flex items-center gap-2">
              <NavigationLink
                className="hidden md:flex"
                href="/login"
                isCurrent={pathIsCurrent(pathname, '/login')}
              >
                Sign in
              </NavigationLink>
              <ButtonLink href="/register" size="small" tone="brand">
                Register
              </ButtonLink>
            </div>
          )}
        </div>
      </div>
      {footer}
    </header>
  );
}

export function AppShell({ children }: AppShellProps) {
  const pathname = usePathname();
  const isChallengeWorkspace = pathname === '/challenges';

  return (
    <div className="kitsune-fixed-workspace fixed inset-0 flex flex-col overflow-hidden overscroll-none bg-surface text-text">
      <SkipLink href="#main-content" placement="global">
        Skip to content
      </SkipLink>
      <AppHeader brandLabel={brandName} />
      <main
        id="main-content"
        className={cx(
          'outline-none',
          focusTargetRing,
          isChallengeWorkspace
            ? 'min-h-0 w-full flex-1 overflow-hidden p-3'
            : 'kitsune-scroll-region m-3 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain rounded-md bg-surface-raised p-4 sm:p-6 lg:p-8'
        )}
        data-scroll-region={isChallengeWorkspace ? undefined : ''}
        tabIndex={-1}
      >
        {children}
      </main>
      <ToastRegion />
    </div>
  );
}
